import { IdToken } from '@auth0/auth0-spa-js';
import { auth0Client } from 'src/api/auth0_client';
import { getConfig } from 'src/config/config';
import { getPlatform, getRepoBackend } from 'src/config/environment';
import { USER_INFO_STORAGE_KEY } from 'src/constants/key_constants';
import { UserInfo, UserPermissions } from 'src/hooks/use_auth';
import { DistributionPlatform, RepoBackend } from 'src/types/env';
import { AuthenticationError } from 'src/types/errors';
import { UserRole } from 'src/types/user_roles';
import { ARTERA_ROLE_NAME } from 'src/utils';
import { getEnumValue } from 'src/utils/enum_utils';

const AUTH0_CACHE_KEY_PREFIX = '@@auth0spajs@@';

/**
 * Clears Auth0 SPA SDK cache from localStorage so the next login gets a fresh session.
 * Call on token errors (e.g. 403) to avoid repeated failed token requests.
 */
export function clearAuth0Cache(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const keysToRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(AUTH0_CACHE_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

/**
 * Logs out the user and clears the user info from localStorage and Auth0 cache.
 */
export async function logout(): Promise<void> {
  window.localStorage.removeItem(USER_INFO_STORAGE_KEY);
  clearAuth0Cache();
  await auth0Client.logout();
}

/**
 * Fetches user info from Auth0's ID token claims.
 *
 * @returns UserInfo if successful. Throws AuthenticationError if not authenticated or error occurs.
 * On token errors (e.g. 403), clears Auth0 cache so callers can logout and redirect without retry loops.
 */
export async function getUserInfo(): Promise<UserInfo> {
  const isAuthenticated = await auth0Client.isAuthenticated();
  if (!isAuthenticated) {
    throw new AuthenticationError();
  }

  try {
    // Get both ID token claims and access token
    const [idTokenClaims] = await Promise.all([
      auth0Client.getIdTokenClaims(),
      auth0Client.getTokenSilently(),
    ]);

    if (idTokenClaims) {
      const user = extractUserInfo(idTokenClaims);

      // For fake backend, we need to persist the user info to local storage, so the
      // fake backend can access it.
      if (getRepoBackend() === RepoBackend.Fake) {
        localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(user));
      }

      return user;
    } else {
      // If the ID token claims are not found, throw an authentication error
      throw new AuthenticationError();
    }
  } catch {
    // Clear cache on any token error (e.g. 403 Forbidden) so we don't retry with a bad refresh token
    clearAuth0Cache();
    throw new AuthenticationError();
  }
}

// Helper function to extract user info from token claims
const extractUserInfo = (claims: IdToken): UserInfo => {
  const namespace = getConfig().auth0Namespace;

  return {
    id: claims.sub,
    email: claims.email!,
    name: claims.name || claims.nickname || claims.email!,
    picture: claims.picture,
    roles: claims[`${namespace}/roles`].map((roleName: ARTERA_ROLE_NAME) =>
      getEnumValue(UserRole, roleName),
    ),
    permissions: extractPermissions(claims),
    orgDisplayName: claims[`${namespace}/org_display_name`],
  };
};

// Helper function to extract permissions from token claims
const extractPermissions = (claims: IdToken): UserPermissions => {
  const permissions = claims[`${getConfig().auth0Namespace}/permissions`] || [];

  // LDT has different permission names than other platforms.
  if (getPlatform() === DistributionPlatform.Ldt) {
    return {
      canCreateOrders: permissions.includes('create_order'),
    };
  } else if (getPlatform() === DistributionPlatform.Samd) {
    return {
      canCreateOrders: permissions.includes('create:orders'),
    };
  }

  throw new Error(`Unsupported platform: ${getPlatform()}`);
};
